import { useState } from 'react';
import { Copy, Check, Link, ExternalLink, Globe } from 'lucide-react';
import { WorkspaceFile } from '../../types';
import { Modal } from '../Common';

interface SharePopupProps {
  file: WorkspaceFile;
  workspace: string;
  cdnUrl: string;
  githubUrl?: string;
  isOpen: boolean;
  onClose: () => void;
}

export function SharePopup({
  file,
  workspace,
  cdnUrl,
  githubUrl,
  isOpen,
  onClose,
}: SharePopupProps) {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const copyToClipboard = async (url: string, type: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(type);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share File" size="md">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Link className="w-5 h-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{file.name}</p>
            <p className="text-sm text-gray-500 truncate">{file.path}</p>
          </div>
        </div>

        {/* CDN URL */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Globe className="w-4 h-4" />
            CDN URL (Public)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={cdnUrl}
              readOnly
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono"
            />
            <button
              onClick={() => copyToClipboard(cdnUrl, 'cdn')}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {copiedUrl === 'cdn' ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
            <a
              href={cdnUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Direct public URL - no authentication required
          </p>
        </div>

        {/* GitHub URL */}
        {githubUrl && (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              GitHub Raw URL
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={githubUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono"
              />
              <button
                onClick={() => copyToClipboard(githubUrl, 'github')}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {copiedUrl === 'github' ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Embed code */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Embed Code
          </label>
          <div className="relative">
            <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">
              {file.name.endsWith('.md') || file.name.endsWith('.txt')
                ? `<iframe src="${cdnUrl}" width="100%" height="400"></iframe>`
                : file.name.match(/\.(png|jpg|jpeg|gif|svg|webp)$/i)
                ? `<img src="${cdnUrl}" alt="${file.name}" />`
                : `<a href="${cdnUrl}" target="_blank">${file.name}</a>`}
            </pre>
            <button
              onClick={() =>
                copyToClipboard(
                  file.name.endsWith('.md') || file.name.endsWith('.txt')
                    ? `<iframe src="${cdnUrl}" width="100%" height="400"></iframe>`
                    : file.name.match(/\.(png|jpg|jpeg|gif|svg|webp)$/i)
                    ? `<img src="${cdnUrl}" alt="${file.name}" />`
                    : `<a href="${cdnUrl}" target="_blank">${file.name}</a>`,
                  'embed'
                )
              }
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white"
            >
              {copiedUrl === 'embed' ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
